/**
 * UnifiedSelectPopover — the business-logic container around the presentational
 * `AvailabilitySelectPopoverContent` shell.
 *
 * Renders for a `multiSelect` `AvailabilityGrid` selection of `free` / `option-own` /
 * `reserved-own` cells (and held-by-other cells that may join a waitlist): a selection read-out,
 * an offer picker (unless the grid is already scoped to one offer) and Rezerwacja / Opcja /
 * Zwolnij actions wired to `batch-reserve` / `batch-release`.
 *
 * Callers resolve the grid-level `MultiCellSelection` into concrete batch-call data via
 * `resolveCell`, and own the anchor `<td>` ref, the offer-picker element and the post-batch
 * refetch and selection recompute. This component never clears the selection after a successful
 * batch call (the popover stays open on the recomputed selection); only Escape and Wyczyść clear
 * it, with no network call.
 *
 * Permission gating is fail-closed: `reservation.manage` resolves with `loading: 'closed'`, the
 * inverse of the plug's usual fail-open default.
 */
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Popover, PopoverAnchor, PopoverContent } from '@ooh/ui/components/popover';
import { cn } from '@ooh/ui/lib/utils';
import { useHasPermission } from '../../plugs/current-user';
import { useBatchRelease, useBatchReserve } from '../../reservations/api/batch-mutations';
import { POPOVER_CONTENT_CLAMP_CLASS } from '../components/cell-popover-state';
import { resolveDeadlineFieldNode, useTtlCountdown } from '../lib/ttl-countdown';
import { AvailabilitySelectPopoverContent } from './availability-select-popover-content';
import {
  computeSuppressOutsideClose,
  POPOVER_WIDTH_CLASS_COMPACT,
  POPOVER_WIDTH_CLASS_WITH_PICKER,
  resolveOutsideDismissHandler,
  resolvePointerDownOutsideHandler,
  resolvePopoverCollisionProps,
  resolvePopoverMaxWidthPx,
  useAnchorPositionStyle,
} from './popover-position';
import { resolveQueueJoinNode, ReserveResultPanel, UnifiedSelectActions } from './popover-parts';
import type { UnifiedSelectPopoverProps } from './popover-types';
import { ReleaseConfirmDialog } from './release-confirm-dialog';
import { ReserveDialogs } from './reserve-dialogs';
import { computeActionGates, resolveSelection } from './selection-helpers';
import { useUnifiedSelectRelease } from './use-unified-select-release';
import { useUnifiedSelectReserve } from './use-unified-select-reserve';

export type { UnifiedSelectPopoverOfferScope, UnifiedSelectPopoverProps } from './popover-types';

export function UnifiedSelectPopover({
  selection,
  resolveCell,
  anchorRef,
  anchorVersion = 0,
  offerScope = null,
  offerPicker = null,
  chosenOfferId = null,
  suppressOutsideDismiss = false,
  onClear,
  onBatchReserveSuccess,
  onBatchReleaseSuccess,
  resolveMediaUnitLabel,
  queueJoinSection,
  queueJoinLoading = false,
  pinBelowAnchor,
}: UnifiedSelectPopoverProps) {
  const { t } = useTranslation('inventory');
  const { t: tReservations } = useTranslation('reservations');
  const canManage = useHasPermission('reservation.manage', { loading: 'closed' });
  const batchReserve = useBatchReserve();
  const batchRelease = useBatchRelease();
  const hasOfferPicker = offerPicker !== null;

  const anchorStyle = useAnchorPositionStyle(
    anchorRef,
    selection.anchor.unitId,
    selection.anchor.periodId,
    anchorVersion,
    resolvePopoverMaxWidthPx(pinBelowAnchor, hasOfferPicker),
  );

  const { unitCount, cellCount, freeCells, heldByOtherCells, releasableCells } = resolveSelection(
    selection,
    resolveCell,
  );
  const allResolvedCells = [...freeCells, ...heldByOtherCells, ...releasableCells];

  const { effectiveOfferId, canSubmitReserve, canRelease } = computeActionGates(
    canManage,
    offerScope,
    chosenOfferId,
    {
      free: freeCells.length,
      releasable: releasableCells.length,
      heldByOther: heldByOtherCells.length,
    },
    { reserve: batchReserve.isPending, release: batchRelease.isPending },
  );

  const reserve = useUnifiedSelectReserve({
    canSubmitReserve,
    effectiveOfferId,
    freeCells,
    allResolvedCells,
    batchReserve,
    onBatchReserveSuccess,
  });
  const release = useUnifiedSelectRelease({
    releasableCells,
    heldByOtherCount: heldByOtherCells.length,
    allResolvedCells,
    canRelease,
    batchRelease,
    resolveMediaUnitLabel,
    onBatchReleaseSuccess,
  });

  const suppressOutsideClose = computeSuppressOutsideClose(
    release.confirming,
    reserve.optionDialogOpen,
    reserve.reserveDialogOpen,
    suppressOutsideDismiss,
  );

  const deadlineNode = useSingleCellDeadlineNode(cellCount, releasableCells, {
    noDeadlineSet: tReservations('deadline.noDeadlineSet'),
    expiredMarker: tReservations('deadline.expiredMarker'),
  });

  const queueJoinNode = resolveQueueJoinNode(
    releasableCells.length,
    queueJoinSection,
    queueJoinLoading,
    {
      disabledHint: t('availability.select.queueJoinDisabledHint'),
      addAnotherClientLabel: tReservations('waitlist.addAnotherClientLabel'),
    },
  );
  const resultNode = reserve.reserveResult ? (
    <ReserveResultPanel result={reserve.reserveResult} onDismiss={reserve.dismissReserveResult} />
  ) : (
    release.resultNode
  );

  return (
    <>
      <Popover open={true}>
        <PopoverAnchor asChild>
          <span style={anchorStyle} data-testid="unified-select-popover-anchor" />
        </PopoverAnchor>
        <PopoverContent
          align="start"
          sideOffset={6}
          // The `size` middleware behind POPOVER_CONTENT_CLAMP_CLASS' available-height clamp runs
          // regardless of avoidCollisions, so the clamp still works when collisions are pinned off.
          {...resolvePopoverCollisionProps(pinBelowAnchor)}
          collisionPadding={8}
          // A sibling ModalDialog (release confirmation, Opcja, Rezerwacja, or the caller's
          // offer picker) portals to document.body, outside this PopoverContent. Radix would
          // treat a click or Escape in it as "outside", clear the selection and unmount the
          // dialog before its own onClick fired; while one is open its own backdrop and Escape
          // listener are the only dismiss path.
          onPointerDownOutside={resolvePointerDownOutsideHandler(suppressOutsideClose, onClear)}
          onEscapeKeyDown={resolveOutsideDismissHandler(suppressOutsideClose, onClear)}
          // Only the embedded offer picker needs the wide variant; offer-scoped callers render a
          // one-line summary plus the action grid and stay compact. The max-width and height
          // bounds keep the popover inside the viewport, which Radix's shift cannot do for an
          // unclamped box (the queue-join section can make the content tall).
          className={cn(
            `p-0 whitespace-normal max-w-[calc(100vw-2rem)] ${POPOVER_CONTENT_CLAMP_CLASS}`,
            hasOfferPicker ? POPOVER_WIDTH_CLASS_WITH_PICKER : POPOVER_WIDTH_CLASS_COMPACT,
          )}
          onClick={(e) => e.stopPropagation()}
          data-testid="unified-select-popover"
        >
          <AvailabilitySelectPopoverContent
            periodCount={cellCount}
            unitCount={unitCount}
            offerSection={offerPicker}
            deadlineNode={deadlineNode}
            resultNode={resultNode}
            queueJoinSection={queueJoinNode}
            actions={
              <UnifiedSelectActions
                canSubmitReserve={canSubmitReserve}
                canRelease={canRelease}
                onOption={reserve.openOptionDialog}
                onReserve={reserve.openReserveDialog}
                onRelease={release.handleReleaseClick}
              />
            }
            onClear={onClear}
          />
        </PopoverContent>
      </Popover>

      {release.dialogProps ? <ReleaseConfirmDialog {...release.dialogProps} /> : null}
      <ReserveDialogs
        reserve={reserve}
        freeCells={freeCells}
        heldByOtherCount={heldByOtherCells.length}
        ownHeldCount={releasableCells.length}
        disabled={!canSubmitReserve}
      />
    </>
  );
}

/**
 * A single own SOFT/HARD reservation reports its expiry deadline like `InspectPopover` does. A
 * multi-cell selection has no single deadline and a free cell has none yet, so both get
 * `undefined` (no row). `ttlExpiresAt` is memoized by its raw string so the countdown interval
 * does not restart on every render.
 */
function useSingleCellDeadlineNode(
  cellCount: number,
  releasableCells: ReturnType<typeof resolveSelection>['releasableCells'],
  labels: { noDeadlineSet: string; expiredMarker: string },
): React.ReactNode | undefined {
  const single = cellCount === 1 && releasableCells.length === 1 ? releasableCells[0] : undefined;
  const ttlExpiresAtRaw = single?.ownReservation.ttlExpiresAt;
  const ttlExpiresAt = React.useMemo(
    () => (ttlExpiresAtRaw ? new Date(ttlExpiresAtRaw) : null),
    [ttlExpiresAtRaw],
  );
  const remaining = useTtlCountdown(ttlExpiresAt);
  if (!single) return undefined;
  return resolveDeadlineFieldNode(
    single.ownReservation.status,
    single.ownReservation.ttlExpiresAt,
    remaining,
    labels,
  );
}
